import { Check, Crown, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { api, UPGRADE_EVENT } from "@/lib/api";
import { isStoreApp } from "@/lib/platform";
import { PLAN_NAMES, type FeatureId, type PlanId } from "@/lib/types";
import { t } from "@shared/i18n";

/** Ce que chaque formule apporte en plus (affiché dans la fenêtre « Passer à… »). */
export const PLAN_PERKS: Record<Exclude<PlanId, "free">, string[]> = {
  starter: [t("100 requêtes IA par jour"), t("Votre logo et vos couleurs sur devis et factures"), t("Google, Slack et Notion connectés"), t("Studio marketing illimité"), t("Idées de croissance chaque semaine"), t("Rappels par e-mail et mode vocal")],
  pro: [t("300 requêtes IA par jour"), t("Tout Essentiel"), t("Dictaphone et transcription de réunions"), t("API pour Zapier, Make, n8n"), t("5 Go de stockage")],
  business: [t("1 000 requêtes IA par jour"), t("Tout Pro"), t("Équipe jusqu'à 50 membres"), t("20 Go de stockage")],
};
export const PLAN_PRICE_HINT: Record<PlanId, string> = { free: t("0 €"), starter: t("9 € / mois"), pro: t("19 € / mois"), business: t("39 € / mois") };

/** Ouvre la fenêtre de passage à une formule supérieure depuis n'importe quel écran. */
export function askUpgrade(feature: FeatureId | null, plan: Exclude<PlanId, "free">, message?: string) {
  window.dispatchEvent(new CustomEvent(UPGRADE_EVENT, { detail: { feature, plan, message } }));
}

export function UpgradeDialog() {
  const [state, setState] = useState<{ plan: Exclude<PlanId, "free">; message?: string } | null>(null);
  const navigate = useNavigate();
  useEffect(() => {
    const on = (e: Event) => {
      const d = (e as CustomEvent<{ plan?: PlanId; feature?: FeatureId | null; message?: string }>).detail ?? {};
      const plan = d.plan && d.plan !== "free" ? d.plan : "starter";
      setState({ plan, message: d.message });
      // Présentation des formules par e-mail (si accepté) : l'achat se fait sur le site, jamais dans les applications des stores.
      void api("/api/billing/upgrade-interest", { method: "POST", json: { plan, feature: d.feature ?? null } }).catch(() => undefined);
    };
    window.addEventListener(UPGRADE_EVENT, on);
    return () => window.removeEventListener(UPGRADE_EVENT, on);
  }, []);
  if (!state) return null;
  const name = PLAN_NAMES[state.plan];
  return (
    <Dialog open onOpenChange={(o) => !o && setState(null)} title={t("Disponible avec {0}", name)} description={state.message ?? t("Débloquez cette fonctionnalité et bien plus encore.")} className="max-w-md">
      <div className="space-y-4">
        <div className="rounded-2xl bg-gradient-to-br from-brand/25 via-brand/10 to-transparent p-4">
          <p className="flex items-center gap-2 text-sm font-bold">
            {state.plan === "starter" ? <Sparkles className="size-4 text-primary" /> : <Crown className="size-4 text-primary" />} {t("Formule")} {name}
            {!isStoreApp && <span className="ml-auto text-[13px] font-semibold text-muted">{PLAN_PRICE_HINT[state.plan]}</span>}
          </p>
          <ul className="mt-3 space-y-1.5 text-[13px]">
            {PLAN_PERKS[state.plan].map((p) => (
              <li key={p} className="flex gap-2"><Check className="mt-0.5 size-4 shrink-0 text-success" />{p}</li>
            ))}
          </ul>
        </div>
        {isStoreApp ? (
          <p className="text-[13px] text-muted">{t("Cette fonctionnalité fait partie de la formule")} {name}{t(". Les abonnements ne sont pas gérés dans cette application.")}</p>
        ) : (
          <>
            <Button className="w-full" onClick={() => { setState(null); navigate("/app/settings/subscription"); }}>{t("Voir les formules")}</Button>
            <p className="text-center text-[12px] text-subtle">{t("Sans engagement, résiliable à tout moment.")}</p>
          </>
        )}
      </div>
    </Dialog>
  );
}
